import { useRef, useState } from "react";
import type { EventOption } from "../../types";
import { START_STEP, hiddenChoices, stepChoices } from "../../utils/stepOrder";
import { pickStepButtons } from "../../hooks/useStepLogger";
import "./StepOrderSheet.css";

const nameOf = (o: EventOption | undefined) => o?.displayName.trim() || "Untitled step";

interface StepButtonsEditorProps {
  options: EventOption[];
  /** The point being edited: a step's id, or START_STEP. */
  stepId: string;
  /** The new buttons at this point, in order. */
  onChange: (ids: string[]) => void;
}

/**
 * The buttons offered at one point of an activity: a preview as the live card
 * draws them, then the list to drag (or arrow) into order, × to take one off
 * (it stays under More…) and + to put one back.
 */
export function StepButtonsEditor({ options, stepId, onChange }: StepButtonsEditorProps) {
  const byId = new Map(options.map((o) => [o.id, o] as const));
  const current = stepChoices(options, stepId).filter((id) => byId.has(id));
  const hidden = hiddenChoices(options, stepId);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= current.length || from === to) return;
    const next = [...current];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    onChange(next);
  };

  // Drag by the handle. Rows are re-measured on every move because they
  // reorder underneath the pointer as it goes.
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const dragging = useRef<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const onHandleDown = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    dragging.current = index;
    setDragIndex(index);
  };
  const onHandleMove = (e: React.PointerEvent) => {
    const from = dragging.current;
    if (from === null) return;
    const to = rowRefs.current.findIndex((row) => {
      if (!row) return false;
      const r = row.getBoundingClientRect();
      return e.clientY >= r.top && e.clientY <= r.bottom;
    });
    if (to >= 0 && to !== from) {
      move(from, to);
      dragging.current = to;
      setDragIndex(to);
    }
  };
  const onHandleUp = () => {
    dragging.current = null;
    setDragIndex(null);
  };

  const preview = pickStepButtons(
    current.map((id) => byId.get(id)!),
    current.length > 1,
  );
  const onCard = new Set([preview.primary?.id, ...preview.secondary.map((o) => o.id)]);

  return (
    <div className="so-buttons">
      <div className="so-preview" aria-label="Buttons you'll see">
        {preview.primary ? (
          <>
            <span className="so-btn so-btn--primary">{nameOf(preview.primary)}</span>
            {preview.secondary.map((o) => (
              <span key={o.id} className="so-btn">{nameOf(o)}</span>
            ))}
          </>
        ) : (
          <span className="so-empty">No buttons: the activity ends here.</span>
        )}
      </div>

      {current.length > 0 && (
        <ol className="so-list" aria-label="Order">
          {current.map((id, i) => (
            <li
              key={id}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              className={`so-row${dragIndex === i ? " is-dragging" : ""}`}
            >
              <span
                className="so-handle"
                aria-hidden="true"
                onPointerDown={onHandleDown(i)}
                onPointerMove={onHandleMove}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
              >
                ⠿
              </span>
              <span className="so-name">
                {nameOf(byId.get(id))}
                {onCard.has(id) && (
                  <span className="so-tag">{preview.primary?.id === id ? "big button" : "on the card"}</span>
                )}
              </span>
              <button type="button" className="so-icon" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`Move ${nameOf(byId.get(id))} up`}>
                ↑
              </button>
              <button
                type="button"
                className="so-icon"
                onClick={() => move(i, i + 1)}
                disabled={i === current.length - 1}
                aria-label={`Move ${nameOf(byId.get(id))} down`}
              >
                ↓
              </button>
              <button
                type="button"
                className="so-icon"
                onClick={() => onChange(current.filter((c) => c !== id))}
                disabled={stepId === START_STEP && current.length === 1}
                aria-label={`Don't offer ${nameOf(byId.get(id))} here`}
              >
                ×
              </button>
            </li>
          ))}
        </ol>
      )}

      {hidden.length > 0 && (
        <div className="so-hidden">
          <span className="so-hint">{current.length === 0 ? "Add a button:" : "Not offered here (still under More…)"}</span>
          <div className="so-chips">
            {hidden.map((o) => (
              <button key={o.id} type="button" className="so-chip" onClick={() => onChange([...current, o.id])}>
                + {nameOf(o)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
